import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Shuffle, 
  Gift, 
  Users, 
  Clock, 
  Trophy,
  Loader2,
  User,
  Shield,
  MapPin,
  PartyPopper,
  Crown,
  Hash
} from "lucide-react";
import { toast } from "sonner";
import { publicAPI } from "../lib/api";
import { formatDate } from "../lib/utils";
import AnimatedFooter from "../components/AnimatedFooter";

// Generate a short reference from participant ID
const generateRef = (id) => {
  if (!id) return "000";
  return id.substring(0, 6).toUpperCase();
};

export default function PublicDrawResults() {
  const { id } = useParams();
  const [draw, setDraw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealedPrizes, setRevealedPrizes] = useState([]);
  const [isRevealing, setIsRevealing] = useState(false);
  const hasRevealedRef = useRef(false);

  useEffect(() => {
    loadDraw();
    const interval = setInterval(loadDraw, 5000);
    return () => clearInterval(interval);
  }, [id]);

  const loadDraw = async () => {
    try {
      const res = await publicAPI.getDraw(id);
      setDraw(res.data);
      
      // Group results by prize to show all winners together
      if (res.data.status === "completed" && res.data.results?.length > 0 && !hasRevealedRef.current) {
        hasRevealedRef.current = true;
        
        // Group results by prize_id
        const resultsByPrize = {};
        (res.data.results || []).forEach(r => {
          const prizeId = r.prize_id || r.prize?.id;
          if (!resultsByPrize[prizeId]) {
            resultsByPrize[prizeId] = {
              prize: r.prize || { id: prizeId, name: r.prize_name },
              winners: []
            };
          }
          resultsByPrize[prizeId].winners.push({
            participant_id: r.participant_id || r.participant?.id,
            participant_name: r.participant_name || r.participant?.name,
            timestamp: r.timestamp
          });
        });
        
        // Convert to array sorted by prize order
        const prizeResults = Object.values(resultsByPrize);
        revealPrizesSequentially(prizeResults);
      }
    } catch (error) {
      toast.error("Tirage non trouvé");
    } finally {
      setLoading(false);
    }
  };

  const fireConfetti = useCallback(() => {
    const count = 200;
    const defaults = { origin: { y: 0.7 }, zIndex: 9999 };

    function fire(particleRatio, opts) {
      confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio),
      });
    }

    fire(0.25, { spread: 26, startVelocity: 55 });
    fire(0.2, { spread: 60 });
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
    fire(0.1, { spread: 120, startVelocity: 45 });
  }, []);

  const revealPrizesSequentially = async (prizeResults) => {
    if (isRevealing) return;
    setIsRevealing(true);
    
    for (let i = 0; i < prizeResults.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 2500));
      setRevealedPrizes(prev => [...prev, prizeResults[i]]);
      fireConfetti();
    }
    
    setIsRevealing(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <Loader2 className="w-12 h-12 text-white animate-spin" />
      </div>
    );
  }

  if (!draw) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <Card className="bg-white/10 backdrop-blur border-white/20">
          <CardContent className="p-8 text-center">
            <Shuffle className="w-16 h-16 text-white/50 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white">Tirage non trouvé</h2>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isCompleted = draw.status === "completed";
  const participantCount = draw.participants?.length || 0;

  // Build results map for quick lookup
  const resultsMap = {};
  (draw.results || []).forEach(r => {
    resultsMap[r.prize_id] = r;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header with Logo */}
        <div className="text-center mb-8">
          {draw.organizer_logo ? (
            <div className="mb-6">
              <img 
                src={draw.organizer_logo} 
                alt={draw.organizer_name}
                className="h-24 mx-auto object-contain"
              />
            </div>
          ) : (
            <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <Shuffle className="w-10 h-10 text-white" />
            </div>
          )}
          
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit' }}>
            {draw.title}
          </h1>
          <p className="text-white/70 text-lg">Organisé par <span className="text-white font-medium">{draw.organizer_name}</span></p>
          
          <div className="flex items-center justify-center gap-4 mt-4">
            <Badge className={`px-4 py-1 text-sm ${isCompleted ? "bg-green-500" : "bg-blue-500"} text-white`}>
              {isCompleted ? "Tirage terminé" : "En cours"}
            </Badge>
            {draw.draw_timestamp && (
              <span className="text-white/60 text-sm flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {formatDate(draw.draw_timestamp)}
              </span>
            )}
          </div>
        </div>

        {/* Live Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card className="bg-white/10 backdrop-blur border-white/20">
            <CardContent className="p-4 text-center">
              <Users className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              <p className="text-3xl font-bold text-white">{participantCount}</p>
              <p className="text-white/60 text-sm">Participants</p>
            </CardContent>
          </Card>
          
          <Card className="bg-white/10 backdrop-blur border-white/20">
            <CardContent className="p-4 text-center">
              <Gift className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <p className="text-3xl font-bold text-white">{draw.prizes?.length || 0}</p>
              <p className="text-white/60 text-sm">Lots à gagner</p>
            </CardContent>
          </Card>
          
          <Card className="bg-white/10 backdrop-blur border-white/20">
            <CardContent className="p-4 text-center">
              <Trophy className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
              <p className="text-3xl font-bold text-white">{draw.results?.length || 0}</p>
              <p className="text-white/60 text-sm">Gagnants</p>
            </CardContent>
          </Card>
        </div>

        {/* Prizes & Winners Section */}
        {isCompleted ? (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-white text-center flex items-center justify-center gap-3">
              <PartyPopper className="w-7 h-7 text-yellow-400" />
              Résultats du Tirage
              <PartyPopper className="w-7 h-7 text-yellow-400" />
            </h2>
            
            <div className="space-y-6">
              <AnimatePresence mode="popLayout">
                {revealedPrizes.map(({ prize, winners }, index) => (
                  <motion.div
                    key={prize?.id || index}
                    initial={{ opacity: 0, scale: 0.8, y: 50 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ 
                      type: "spring",
                      stiffness: 200,
                      damping: 20
                    }}
                  >
                    <Card className={`overflow-hidden ${
                      winners?.length > 0 
                        ? "bg-gradient-to-br from-yellow-500/30 to-amber-600/30 border-yellow-500/50" 
                        : "bg-white/10 border-white/20"
                    }`}>
                      <CardContent className="p-0">
                        {/* Prize Header */}
                        <div className={`p-5 ${winners?.length > 0 ? "bg-gradient-to-r from-yellow-500/20 to-amber-500/20" : "bg-white/5"}`}>
                          <div className="flex items-center gap-4">
                            <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-xl font-bold ${
                              winners?.length > 0 
                                ? "bg-gradient-to-br from-yellow-400 to-amber-500 shadow-lg shadow-yellow-500/30 text-white" 
                                : "bg-white/10 text-white/70"
                            }`}>
                              {index + 1}
                            </div>
                            <div className="flex-1">
                              <h3 className="text-xl font-bold text-white">{prize?.name}</h3>
                              {prize?.description && (
                                <p className="text-white/60 text-sm">{prize?.description}</p>
                              )}
                            </div>
                            <Badge className={`text-lg px-3 py-1 ${winners?.length > 0 ? "bg-yellow-500/30 text-yellow-200" : "bg-white/20 text-white"}`}>
                              {winners?.length || 0} gagnant{(winners?.length || 0) > 1 ? 's' : ''}
                            </Badge>
                          </div>
                        </div>
                        
                        {/* Winners Section */}
                        <div className="p-5">
                          {winners?.length > 0 ? (
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {winners.map((winner, wIndex) => (
                                <div key={winner.participant_id} className="flex items-center gap-3 bg-white/5 rounded-xl p-4 border border-white/10">
                                  <div className="relative flex-shrink-0">
                                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center shadow-lg text-xl font-bold text-white">
                                      {wIndex + 1}
                                    </div>
                                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                                      <Crown className="w-3 h-3 text-white" />
                                    </div>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h4 className="text-lg font-bold text-white truncate">
                                      {winner.participant_name}
                                    </h4>
                                    <p className="text-white/50 text-sm flex items-center gap-1">
                                      <Hash className="w-3 h-3" />
                                      Réf: {generateRef(winner.participant_id)}
                                    </p>
                                  </div>
                                  <Trophy className="w-6 h-6 text-yellow-400 flex-shrink-0" />
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-6 text-white/50">
                              <User className="w-10 h-10 mx-auto mb-2 opacity-50" />
                              <p>Lot non attribué</p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
            
            {/* Revealing animation */}
            {isRevealing && revealedPrizes.length < Object.keys(resultsMap).length && (
              <div className="text-center py-8">
                <motion.div
                  animate={{ scale: [1, 1.2, 1], rotate: [0, 180, 360] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                >
                  <Gift className="w-16 h-16 text-yellow-400 mx-auto" />
                </motion.div>
                <p className="text-white/70 mt-4 text-lg">Révélation du prochain lot...</p>
              </div>
            )}
          </div>
        ) : (
          /* Waiting for draw - Show participants and prizes */
          <div className="space-y-8">
            {/* Prizes to win */}
            {draw.prizes?.length > 0 && (
              <Card className="bg-white/10 backdrop-blur border-white/20">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                    <Gift className="w-6 h-6 text-amber-400" />
                    Lots à gagner
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {draw.prizes.map((prize, index) => (
                      <div 
                        key={prize.id}
                        className="flex items-center gap-4 bg-white/5 rounded-xl p-4 border border-white/10"
                      >
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <p className="text-white font-semibold text-lg">{prize.name}</p>
                          {prize.description && (
                            <p className="text-white/60 text-sm">{prize.description}</p>
                          )}
                        </div>
                        <Badge variant="secondary" className="bg-white/10 text-white">
                          x{prize.quantity}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Participants list */}
            <Card className="bg-white/10 backdrop-blur border-white/20">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Participants inscrits ({participantCount})
                </h3>
                
                {participantCount === 0 ? (
                  <p className="text-white/60 text-center py-8">
                    Aucun participant pour le moment...
                  </p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-96 overflow-y-auto">
                    {draw.participants?.map((p, index) => (
                      <motion.div
                        key={p.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.03 }}
                        className="bg-white/5 rounded-lg p-3 border border-white/10"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 rounded-full bg-blue-500/30 flex items-center justify-center">
                            <User className="w-5 h-5 text-blue-300" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-white text-sm font-medium block truncate">{p.name}</span>
                            <span className="text-white/40 text-xs flex items-center gap-1">
                              <Hash className="w-3 h-3" />
                              {generateRef(p.id)}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Waiting message */}
            <div className="text-center py-8">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 3, ease: "linear" }}
              >
                <Clock className="w-12 h-12 text-white/50 mx-auto" />
              </motion.div>
              <p className="text-white/70 mt-4">En attente du tirage...</p>
              <p className="text-white/50 text-sm">Cette page se met à jour automatiquement</p>
            </div>
          </div>
        )}

        {/* Organizer Info */}
        {(draw.bailiff_name || draw.withdrawal_instructions) && (
          <Card className="bg-white/10 backdrop-blur border-white/20 mt-8">
            <CardContent className="p-6 space-y-4">
              {draw.bailiff_name && (
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-blue-400 mt-0.5" />
                  <div>
                    <p className="text-white/60 text-sm">Supervisé par</p>
                    <p className="text-white font-medium">{draw.bailiff_name}</p>
                    {draw.bailiff_info && (
                      <p className="text-white/70 text-sm">{draw.bailiff_info}</p>
                    )}
                  </div>
                </div>
              )}
              
              {draw.withdrawal_instructions && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-green-400 mt-0.5" />
                  <div>
                    <p className="text-white/60 text-sm">Retrait des lots</p>
                    <p className="text-white">{draw.withdrawal_instructions}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Partner Logos Carousel */}
        {draw.partner_carousel_enabled && draw.partner_logos?.length > 0 && (
          <Card className="bg-white/10 backdrop-blur border-white/20 mt-8 overflow-hidden">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-white text-center mb-6">
                {draw.partner_carousel_title || "Merci à nos partenaires"}
              </h3>
              <div className="relative overflow-hidden">
                <div className="flex items-center justify-center gap-8 flex-wrap">
                  {draw.partner_logos.map((logo, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex-shrink-0"
                    >
                      <div className="bg-white rounded-xl p-3 shadow-lg hover:scale-110 transition-transform">
                        <img
                          src={logo.url}
                          alt={logo.name || `Partenaire ${index + 1}`}
                          className="h-12 w-auto max-w-[120px] object-contain"
                        />
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="mt-8">
          <AnimatedFooter variant="transparent" />
        </div>
      </div>
    </div>
  );
}
